import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';

import { clearClinicalNote } from 'api/ClinicalNote';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import { clinicalNote as clinicalNoteState } from 'components/state';

import { useClinicalNoteContext } from '../../../../../../lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { useMacrosContext } from '../../../../../../lib/context/MacrosContext/MacrosContext';
import PreviousNotes from '../../LoadFrom/PreviousNotes';
import CreateMacro from '../../Sidebar/Macros/Create/CreateMacro';
import LoadMacros from '../../Sidebar/Macros/Load/LoadMacros';
import TPModal from '../../TreatmentPlan/TPModal';
import { showPreviewNote } from '../../lib/showPreviewNote';
import { popoverOptionsContextMenu } from '../lib/popoverOptions';

import { handleShowNavbarElements } from './lib/handleShowNavbarElements';

const NavbarContextMenu = ({
  autosave,
  handleAutoSave = () => {},
  onShowConfirmSignLock = () => {},
  userAssignedToDocument,
  userSign = false
}) => {
  const {
    setAdvancedHP,
    setAdvancedSOAP,
    setCustomFormTypes,
    showPrevNoteModal,
    setShowPrevNoteModal,
    setPreviewNoteModal,
    setIsExport,
    saveNoteRef,
    saveButtonText,
    cnDisplaySettings
  } = useClinicalNoteContext();
  const [clinicalNote, setClinicalNote] = useRecoilState(clinicalNoteState);
  const { loadMacrosModal, setLoadMacrosModal, filters, setFilters } = useMacrosContext();
  const [createMacroModal, setCreateMacroModal] = useState(false);
  const [showPreviewNoteLoading, setShowPreviewNoteLoading] = useState(false);
  const [showExport, setShowExport] = useState(window.innerWidth < 970);
  const [showLock, setShowLock] = useState(window.innerWidth < 1220);
  const [showAutoSave, setShowAutoSave] = useState(window.innerWidth < 1089);
  const [showMyScribeAi, setShowMyScribeAi] = useState(window.innerWidth < 768);
  const [showClearNoteConfirmModal, setShowClearNoteConfirmModal] = useState(false);
  const [showTP, setShowTP] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const enabledTp = cnDisplaySettings?.treatment_plan?.enabled;

  const { id, appointmentId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleResize = () => {
      handleShowNavbarElements({
        screenWidth: window?.innerWidth,
        setShowExport,
        setShowAutoSave,
        setShowLock,
        setShowMyScribeAi,
        setShowTP
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleRunPreviewNote = async () => {
    if (showPreviewNoteLoading) {
      return;
    }

    await showPreviewNote({
      patientId: clinicalNote?.user_id || id,
      appointmentId: clinicalNote?.appointment_id || appointmentId,
      saveButtonText,
      exportNote: true,
      setClinicalNote,
      setAdvancedHP,
      setAdvancedSOAP,
      setCustomFormTypes,
      setPreviewNoteModal,
      setIsExport,
      setLoading: setShowPreviewNoteLoading,
      navigate,
      onSaveNote: saveNoteRef?.current?.onSaveNote
    });
  };

  const { mutateAsync: clearNote, isLoading: isClearingNote } = useMutation(
    (data) => clearClinicalNote(navigate, data),
    {
      onSettled: async (data) => {
        const { code, error = null } = data || {};

        if (code !== 0) {
          showAlert({
            title: 'Clear Note',
            message: error ?? 'Clear note process failed! Please contact support.',
            color: 'danger'
          });

          setShowClearNoteConfirmModal(false);
          return;
        }

        showAlert({
          title: 'Clear Note',
          message: 'Note cleared successfully.',
          color: 'success'
        });
        setShowClearNoteConfirmModal(false);
        window.location.reload();
      }
    }
  );

  const handleClinicalNoteClear = async () => {
    await clearNote({
      patientId: id,
      appointmentId: appointmentId || clinicalNote?.appointment_id,
      clinicalNoteId: clinicalNote?.id
    });
  };

  const options = popoverOptionsContextMenu({
    filters,
    setFilters,
    setLoadMacrosModal,
    setCreateMacroModal,
    setShowPrevNoteModal,
    showLock,
    showExport,
    showAutoSave,
    showMyScribeAi,
    noteLocked: clinicalNote?.locked,
    autosave,
    handleAutoSave,
    handleRunPreviewNote,
    setShowClearNoteConfirmModal,
    onShowConfirmSignLock,
    userSign,
    userAssignedToDocument,
    clinicalNoteId: clinicalNote?.id,
    saveButtonText,
    navigate,
    params: { id, appointmentId },
    showTP,
    setShowTPModal,
    enabledTp,
    myscribeAIPath: !!pathname.includes('myscribe-ai/')
  });

  return (
    <>
      <Popover
        isFixed
        position="left"
        options={options}
        icon="context-menu-vertical"
        iconColor="white"
        buttonClassName="flex h-[32px] text-[14px] w-[32px] border-none cursor-pointer items-center justify-center rounded-md  bg-primary-800 hover:bg-primary-700 p-0"
        panelClassName="mt-[6px]"
        data-qa="clinical-notes-menu"
      />

      {createMacroModal && (
        <CreateMacro
          macroTitle="Appointment Macro"
          macroType="appointment_macro"
          open={createMacroModal}
          handleClose={() => setCreateMacroModal(false)}
        />
      )}
      {loadMacrosModal && <LoadMacros type="appointment_macro" />}
      {showPrevNoteModal && <PreviousNotes />}
      {showClearNoteConfirmModal && (
        <Confirm
          handleOpen={showClearNoteConfirmModal}
          iconColor="danger"
          handleClose={() => setShowClearNoteConfirmModal(false)}
          handleContinue={handleClinicalNoteClear}
          title="Clear Note"
          message="Are you sure you want to clear this note? This will permanently remove any data you've entered, but will not delete forms filled out by the patient."
          primaryBtnTxt="Clear"
          variant="danger"
          secondaryBtnTxt="Close"
          icon="note-mono"
          loading={isClearingNote}
        />
      )}
      {showTPModal && <TPModal isOpen={showTPModal} handleClose={() => setShowTPModal(false)} />}
    </>
  );
};

export default NavbarContextMenu;
